import { ChangeDetectionStrategy, Component, computed, effect, inject, OnDestroy, signal } from '@angular/core';
import { BlurImageComponent } from 'src/app/components/blur-image/blur-image.component';
import { AppUpdatePopupService } from 'src/app/services/app-update-popup.service';
import { AppUpdatesStore } from 'src/app/store/app-updates.store';

const AUTOPLAY_MS = 6000;

/**
 * Desktop carousel of the user's active offers (announcements with isOffer),
 * shown above the support hint bubble. Unlike the popups it lists offers the
 * user already saw, so a closed popup can be found again. Clicking a slide
 * opens the full announcement; closing collapses it to a small pill instead of
 * hiding it, so the offers are never lost.
 */
@Component({
	selector: 'app-offers-carousel',
	standalone: true,
	imports: [BlurImageComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
	styleUrl: './offers.scss',
	template: `
		@if (current(); as offer) {
			@if (collapsed()) {
				<button type="button" class="sp-offers-pill" (click)="collapsed.set(false)" aria-label="Ver ofertas">
					<span aria-hidden="true">🎁</span>
					{{ count() === 1 ? '1 oferta' : count() + ' ofertas' }}
				</button>
			} @else {
				<div
					class="sp-offers-card"
					role="region"
					aria-roledescription="carrusel"
					aria-label="Ofertas"
					(mouseenter)="paused.set(true)"
					(mouseleave)="paused.set(false)"
				>
					<button
						type="button"
						class="sp-offers-card__close"
						(click)="collapsed.set(true)"
						aria-label="Minimizar ofertas"
					>×</button>

					<button type="button" class="sp-offers-card__body" (click)="open()" [attr.aria-label]="'Ver oferta: ' + offer.title">
						@if (offer.imageUrl) {
							<app-blur-image
								[src]="offer.imageUrl"
								[placeholder]="offer.imageThumbUrl"
								[alt]="offer.title"
								imgClass="object-cover w-full h-32"
							/>
						}
						<span class="sp-offers-card__text">
							<span class="sp-offers-card__badge">Oferta</span>
							<span class="sp-offers-card__title">{{ offer.title }}</span>
							<span class="sp-offers-card__desc">{{ offer.description }}</span>
						</span>
					</button>

					@if (count() > 1) {
						<div class="sp-offers-card__nav">
							<button type="button" (click)="prev()" aria-label="Oferta anterior">‹</button>
							<span class="sp-offers-card__dots">
								@for (o of offers(); track o._id; let i = $index) {
									<button
										type="button"
										[class.active]="i === index()"
										(click)="index.set(i)"
										[attr.aria-label]="'Oferta ' + (i + 1)"
									></button>
								}
							</span>
							<button type="button" (click)="next()" aria-label="Oferta siguiente">›</button>
						</div>
					}
				</div>
			}
		}
	`,
})
export class OffersCarouselComponent implements OnDestroy {
	private readonly store = inject(AppUpdatesStore);
	private readonly popupService = inject(AppUpdatePopupService);

	readonly offers = this.store.offers;
	readonly count = computed(() => this.offers().length);
	readonly index = signal(0);
	readonly paused = signal(false);
	readonly collapsed = signal(false);
	readonly current = computed(() => {
		const list = this.offers();
		return list.length ? list[this.index() % list.length] : undefined;
	});

	private timer?: ReturnType<typeof setInterval>;

	constructor() {
		// Autoplay only with several offers and while the pointer is not on the card.
		effect(() => {
			this.stop();
			if (this.count() > 1 && !this.paused() && !this.collapsed()) {
				this.timer = setInterval(() => this.next(), AUTOPLAY_MS);
			}
		});
	}

	ngOnDestroy(): void {
		this.stop();
	}

	next(): void {
		this.index.update((i) => (i + 1) % Math.max(1, this.count()));
	}

	prev(): void {
		this.index.update((i) => (i - 1 + this.count()) % Math.max(1, this.count()));
	}

	open(): void {
		const offer = this.current();
		if (offer) this.popupService.openOffer(offer);
	}

	private stop(): void {
		if (this.timer) clearInterval(this.timer);
		this.timer = undefined;
	}
}
